import React, { useState } from 'react';
import {
    Search, Hash, Mail, MapPin, Type, Heading, CheckSquare, Circle,
    Upload, ChevronDown, AlignLeft, LayoutGrid, Square, FileText,
    Camera, Video, Columns, PanelTop, Table, FolderOpen,
    Phone, Link, Calendar, DollarSign, PenTool, Grid, TreePine, FileCode,
    Eye, EyeOff, Database, ListChecks, FormInput, Clock,
    LayoutTemplate, Box, Rows, SlidersHorizontal, GripVertical, List, Grid3X3
} from 'lucide-react';

interface ComponentItem {
    type: string;
    label: string;
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    schema: any;
}

interface FormBuilderSidebarProps {
    onAddComponent: (component: object) => void;
}

// Basic Elements
const basicComponents: ComponentItem[] = [
    { type: 'textfield', label: 'Text Field', icon: <Type size={20} />, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', schema: { type: 'textfield', label: 'Text Field', input: true } },
    { type: 'textarea', label: 'Text Area', icon: <AlignLeft size={20} />, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600', schema: { type: 'textarea', label: 'Text Area', input: true, rows: 3 } },
    { type: 'number', label: 'Number', icon: <Hash size={20} />, iconBg: 'bg-purple-100', iconColor: 'text-purple-600', schema: { type: 'number', label: 'Number', input: true } },
    { type: 'password', label: 'Password', icon: <EyeOff size={20} />, iconBg: 'bg-red-100', iconColor: 'text-red-600', schema: { type: 'password', label: 'Password', input: true } },
    { type: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={20} />, iconBg: 'bg-green-100', iconColor: 'text-green-600', schema: { type: 'checkbox', label: 'Checkbox', input: true } },
    { type: 'selectboxes', label: 'Select Boxes', icon: <ListChecks size={20} />, iconBg: 'bg-teal-100', iconColor: 'text-teal-600', schema: { type: 'selectboxes', label: 'Select Boxes', input: true, values: [{ label: 'Option 1', value: 'option1' }] } },
    { type: 'select', label: 'Select', icon: <ChevronDown size={20} />, iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600', schema: { type: 'select', label: 'Select', input: true, data: { values: [{ label: 'Option 1', value: 'option1' }] } } },
    { type: 'radio', label: 'Radio', icon: <Circle size={20} />, iconBg: 'bg-pink-100', iconColor: 'text-pink-600', schema: { type: 'radio', label: 'Radio', input: true, values: [{ label: 'Option 1', value: 'option1' }] } },
    { type: 'email', label: 'Email', icon: <Mail size={20} />, iconBg: 'bg-orange-100', iconColor: 'text-orange-600', schema: { type: 'email', label: 'Email', input: true } },
    { type: 'phoneNumber', label: 'Phone', icon: <Phone size={20} />, iconBg: 'bg-lime-100', iconColor: 'text-lime-600', schema: { type: 'phoneNumber', label: 'Phone Number', input: true } },
    { type: 'address', label: 'Address', icon: <MapPin size={20} />, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', schema: { type: 'address', label: 'Address', input: true } },
    { type: 'url', label: 'URL', icon: <Link size={20} />, iconBg: 'bg-sky-100', iconColor: 'text-sky-600', schema: { type: 'url', label: 'URL', input: true } },
    { type: 'datetime', label: 'Date/Time', icon: <Calendar size={20} />, iconBg: 'bg-rose-100', iconColor: 'text-rose-600', schema: { type: 'datetime', label: 'Date / Time', input: true, enableDate: true, enableTime: true } },
    { type: 'day', label: 'Day', icon: <Clock size={20} />, iconBg: 'bg-fuchsia-100', iconColor: 'text-fuchsia-600', schema: { type: 'day', label: 'Day', input: true } },
    { type: 'currency', label: 'Currency', icon: <DollarSign size={20} />, iconBg: 'bg-yellow-100', iconColor: 'text-yellow-600', schema: { type: 'currency', label: 'Currency', input: true } },
    { type: 'signature', label: 'Signature', icon: <PenTool size={20} />, iconBg: 'bg-slate-100', iconColor: 'text-slate-600', schema: { type: 'signature', label: 'Signature', input: true } },
    { type: 'button', label: 'Button', icon: <Square size={20} />, iconBg: 'bg-gray-100', iconColor: 'text-gray-600', schema: { type: 'button', label: 'Submit', action: 'submit', theme: 'primary' } },
];

// Layout Groups
const layoutComponents: ComponentItem[] = [
    { type: 'columns', label: 'Columns', icon: <Columns size={20} />, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', schema: { type: 'columns', columns: [{ components: [], width: 6 }, { components: [], width: 6 }] } },
    { type: 'fieldset', label: 'Fieldset', icon: <Box size={20} />, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600', schema: { type: 'fieldset', legend: 'Fieldset', components: [] } },
    { type: 'panel', label: 'Panel', icon: <PanelTop size={20} />, iconBg: 'bg-purple-100', iconColor: 'text-purple-600', schema: { type: 'panel', title: 'Panel', collapsible: false, components: [] } },
    { type: 'table', label: 'Table', icon: <Table size={20} />, iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600', schema: { type: 'table', numRows: 3, numCols: 3, rows: [[{ components: [] }, { components: [] }, { components: [] }], [{ components: [] }, { components: [] }, { components: [] }], [{ components: [] }, { components: [] }, { components: [] }]] } },
    { type: 'tabs', label: 'Tabs', icon: <Rows size={20} />, iconBg: 'bg-teal-100', iconColor: 'text-teal-600', schema: { type: 'tabs', components: [{ label: 'Tab 1', key: 'tab1', components: [] }] } },
    { type: 'well', label: 'Well', icon: <LayoutTemplate size={20} />, iconBg: 'bg-gray-100', iconColor: 'text-gray-600', schema: { type: 'well', components: [] } },
    { type: 'wizardpage', label: 'Wizard', icon: <SlidersHorizontal size={20} />, iconBg: 'bg-violet-100', iconColor: 'text-violet-600', schema: { type: 'panel', title: 'Wizard Page', isWizardPage: true, components: [] } },
    { type: 'container', label: 'Container', icon: <FolderOpen size={20} />, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', schema: { type: 'container', components: [] } },
];

// Advanced Elements
const advancedComponents: ComponentItem[] = [
    { type: 'datagrid', label: 'DataGrid', icon: <Grid size={20} />, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', schema: { type: 'datagrid', label: 'Data Grid', input: true, components: [] } },
    { type: 'editgrid', label: 'EditGrid', icon: <LayoutGrid size={20} />, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600', schema: { type: 'editgrid', label: 'Edit Grid', input: true, components: [] } },
    { type: 'tree', label: 'Tree', icon: <TreePine size={20} />, iconBg: 'bg-green-100', iconColor: 'text-green-600', schema: { type: 'tree', label: 'Tree', input: true, components: [] } },
    { type: 'form', label: 'Nested Form', icon: <FormInput size={20} />, iconBg: 'bg-purple-100', iconColor: 'text-purple-600', schema: { type: 'form', label: 'Nested Form', src: '' } },
    { type: 'htmlelement', label: 'HTML', icon: <FileCode size={20} />, iconBg: 'bg-orange-100', iconColor: 'text-orange-600', schema: { type: 'htmlelement', tag: 'div', content: 'HTML Content', className: '' } },
    { type: 'content', label: 'Content', icon: <Heading size={20} />, iconBg: 'bg-teal-100', iconColor: 'text-teal-600', schema: { type: 'content', html: '<p>Content</p>' } },
    { type: 'custom', label: 'Custom', icon: <Box size={20} />, iconBg: 'bg-pink-100', iconColor: 'text-pink-600', schema: { type: 'custom', customDefaultValue: '', customConditional: '' } },
    { type: 'file', label: 'File', icon: <Upload size={20} />, iconBg: 'bg-cyan-100', iconColor: 'text-cyan-600', schema: { type: 'file', label: 'File Upload', input: true, storage: 'base64' } },
    { type: 'hidden', label: 'Hidden', icon: <Eye size={20} />, iconBg: 'bg-gray-100', iconColor: 'text-gray-600', schema: { type: 'hidden', input: true } },
    { type: 'resource', label: 'Resource', icon: <Database size={20} />, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', schema: { type: 'resource', label: 'Resource', input: true } },
];

// Special Elements
const specialComponents: ComponentItem[] = [
    { type: 'survey', label: 'Survey', icon: <ListChecks size={20} />, iconBg: 'bg-violet-100', iconColor: 'text-violet-600', schema: { type: 'survey', label: 'Survey', input: true, questions: [], values: [] } },
    { type: 'pdfviewer', label: 'PDF Viewer', icon: <FileText size={20} />, iconBg: 'bg-red-100', iconColor: 'text-red-600', schema: { type: 'pdfviewer', label: 'PDF Viewer' } },
    { type: 'takepicture', label: 'Camera', icon: <Camera size={20} />, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', schema: { type: 'takepicture', label: 'Camera' } },
    { type: 'videoplayer', label: 'Video', icon: <Video size={20} />, iconBg: 'bg-fuchsia-100', iconColor: 'text-fuchsia-600', schema: { type: 'videoplayer', label: 'Video Player' } },
];

// Grid Card Component
const GridCard: React.FC<{ item: ComponentItem; onAdd: (schema: object) => void }> = ({ item, onAdd }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleClick = () => {
        const schema = item.schema as any;
        const uniqueKey = `${schema.type || item.type}_${Date.now()}`;
        onAdd({ ...item.schema, key: uniqueKey });
    };

    const handleDragStart = (e: React.DragEvent) => {
        setIsDragging(true);
        const schema = item.schema as any;
        const uniqueKey = `${schema.type || item.type}_${Date.now()}`;
        e.dataTransfer.setData('application/json', JSON.stringify({ ...item.schema, key: uniqueKey }));
        e.dataTransfer.effectAllowed = 'copy';
    };

    return (
        <div
            draggable
            onDragStart={handleDragStart}
            onDragEnd={() => setIsDragging(false)}
            onClick={handleClick}
            className={`flex flex-col items-center justify-center p-3 bg-white border rounded-xl cursor-grab transition-all duration-200 group active:scale-95
                ${isDragging ? 'border-indigo-400 shadow-lg opacity-50' : 'border-gray-100 hover:border-indigo-300 hover:shadow-md'}`}
        >
            <div className={`w-10 h-10 ${item.iconBg} ${item.iconColor} rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                {item.icon}
            </div>
            <span className="text-xs font-medium text-gray-700 text-center leading-tight">{item.label}</span>
        </div>
    );
};

// List Card Component
const ListCard: React.FC<{ item: ComponentItem; onAdd: (schema: object) => void }> = ({ item, onAdd }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleClick = () => {
        const schema = item.schema as any;
        const uniqueKey = `${schema.type || item.type}_${Date.now()}`;
        onAdd({ ...item.schema, key: uniqueKey });
    };

    const handleDragStart = (e: React.DragEvent) => {
        setIsDragging(true);
        const schema = item.schema as any;
        const uniqueKey = `${schema.type || item.type}_${Date.now()}`;
        e.dataTransfer.setData('application/json', JSON.stringify({ ...item.schema, key: uniqueKey }));
        e.dataTransfer.effectAllowed = 'copy';
    };

    return (
        <div
            draggable
            onDragStart={handleDragStart}
            onDragEnd={() => setIsDragging(false)}
            onClick={handleClick}
            className={`flex items-center gap-2 p-2.5 bg-white border rounded-lg cursor-grab transition-all duration-200 group active:cursor-grabbing
                ${isDragging ? 'border-indigo-400 shadow-lg opacity-50' : 'border-gray-100 hover:border-indigo-300 hover:shadow-md'}`}
        >
            <div className="text-gray-300 group-hover:text-gray-400">
                <GripVertical size={14} />
            </div>
            <div className={`w-8 h-8 ${item.iconBg} ${item.iconColor} rounded-md flex items-center justify-center flex-shrink-0`}>
                {item.icon}
            </div>
            <span className="text-sm font-medium text-gray-700 truncate">{item.label}</span>
        </div>
    );
};

interface CollapsibleSectionProps {
    title: string;
    children: React.ReactNode;
    defaultOpen?: boolean;
    count?: number;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultOpen = true, count }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 hover:text-gray-700 py-1"
            >
                <div className="flex items-center gap-2">
                    <span>{title}</span>
                    {count !== undefined && (
                        <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-normal">{count}</span>
                    )}
                </div>
                <ChevronDown size={14} className={`transition-transform ${isOpen ? '' : '-rotate-90'}`} />
            </button>
            {isOpen && children}
        </div>
    );
};

const FormBuilderSidebar: React.FC<FormBuilderSidebarProps> = ({ onAddComponent }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const filterComponents = (components: ComponentItem[]) => {
        if (!searchTerm) return components;
        return components.filter(c => c.label.toLowerCase().includes(searchTerm.toLowerCase()));
    };

    const allFiltered = {
        basic: filterComponents(basicComponents),
        layout: filterComponents(layoutComponents),
        advanced: filterComponents(advancedComponents),
        special: filterComponents(specialComponents),
    };

    const renderComponents = (items: ComponentItem[], keyPrefix: string) => {
        if (viewMode === 'grid') {
            return (
                <div className="grid grid-cols-2 gap-2">
                    {items.map((item, idx) => (
                        <GridCard key={`${keyPrefix}-${idx}`} item={item} onAdd={onAddComponent} />
                    ))}
                </div>
            );
        }
        return (
            <div className="space-y-1.5">
                {items.map((item, idx) => (
                    <ListCard key={`${keyPrefix}-${idx}`} item={item} onAdd={onAddComponent} />
                ))}
            </div>
        );
    };

    return (
        <div className="w-72 bg-gray-50 border-r border-gray-200 p-3 overflow-y-auto h-full flex flex-col">
            {/* Search & View Toggle */}
            <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                    />
                </div>
                <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="Grid view"
                    >
                        <Grid3X3 size={16} />
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="List view"
                    >
                        <List size={16} />
                    </button>
                </div>
            </div>

            {/* Basic Elements - Open by default */}
            {allFiltered.basic.length > 0 && (
                <CollapsibleSection title="Basic" count={allFiltered.basic.length} defaultOpen={true}>
                    {renderComponents(allFiltered.basic, 'basic')}
                </CollapsibleSection>
            )}

            {/* Layout Groups - Collapsed */}
            {allFiltered.layout.length > 0 && (
                <CollapsibleSection title="Layout" count={allFiltered.layout.length} defaultOpen={false}>
                    {renderComponents(allFiltered.layout, 'layout')}
                </CollapsibleSection>
            )}

            {/* Advanced Elements - Collapsed */}
            {allFiltered.advanced.length > 0 && (
                <CollapsibleSection title="Advanced" count={allFiltered.advanced.length} defaultOpen={false}>
                    {renderComponents(allFiltered.advanced, 'advanced')}
                </CollapsibleSection>
            )}

            {/* Special Elements - Collapsed */}
            {allFiltered.special.length > 0 && (
                <CollapsibleSection title="Special" count={allFiltered.special.length} defaultOpen={false}>
                    {renderComponents(allFiltered.special, 'special')}
                </CollapsibleSection>
            )}

        </div>
    );
};

export default FormBuilderSidebar;
